import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { SelectButton } from "primereact/selectbutton";

import { listProvisioning, markProvisioningHandled } from "../../../services/eroundProvisioning";
import { getProspect } from "../../../services/onboarding/prospects";
import { customerName } from "../../../utils/customerName";
import { ProvisioningSummary, QueueRow, WaitingOn } from "../../../types/eroundProvisioning";
import ProvisioningStatusTag from "../Prospects/components/ProvisioningStatusTag";
import { provisioningErrorMessage } from "../Prospects/components/provisioningErrors";

const ALL = [
  "AWAITING_APPROVAL",
  "FAILED",
  "NEEDS_MANUAL_ACTION",
  "COLLECTING",
  "DISPATCHED",
  "IN_PROGRESS",
  "SUCCEEDED",
  "REJECTED",
  "CANCELLED",
  "SUPERSEDED",
];

interface Filter {
  key: string;
  label: string;
  statuses: string[];
  empty: string;
  // Only the stations that wait on this side (SOPS-165, SOPS-196).
  waitingOn?: WaitingOn;
}

// By who acts next (SOPS-196). Rejected, withdrawn, replaced and handled stations are under All only.
export const FILTERS: Filter[] = [
  { key: "all", label: "All", statuses: ALL, empty: "No station is tracked yet." },
  { key: "in-progress", label: "In progress", statuses: ["DISPATCHED", "IN_PROGRESS"], empty: "Nothing is being created right now." },
  {
    key: "support",
    label: "Waiting for support",
    statuses: ["COLLECTING", "AWAITING_APPROVAL", "NEEDS_MANUAL_ACTION"],
    waitingOn: "SUPPORT",
    empty: "No station is waiting for support.",
  },
  {
    key: "customer",
    label: "Waiting for customer",
    statuses: ["COLLECTING"],
    waitingOn: "CUSTOMER",
    empty: "No station is waiting for the customer.",
  },
  { key: "failed", label: "Failed", statuses: ["FAILED"], empty: "No station has failed." },
  { key: "done", label: "In eRound", statuses: ["SUCCEEDED"], empty: "No station has been created in eRound yet." },
];

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

// The summary carries no customer (research R6): it is read once per prospect, with the company for a business.
const enrich = async (records: ProvisioningSummary[]): Promise<QueueRow[]> => {
  const prospectIds = Array.from(new Set(records.map((r) => r.prospectId)));
  const customers = new Map(
    await Promise.all(
      prospectIds.map(async (id) => [id, customerName(await getProspect(id).catch(() => null))] as const)
    )
  );
  return records.map((r) => ({ ...r, customerName: customers.get(r.prospectId) ?? null }));
};

const byOldest = (a: QueueRow, b: QueueRow) => a.updatedAt.localeCompare(b.updatedAt);

const address = (r: QueueRow): string =>
  [r.street, [r.postcode, r.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "—";

const canMarkHandled = (r: ProvisioningSummary) => r.status === "NEEDS_MANUAL_ACTION" && !r.manualActionHandled;

// Who acts next. A waiting station without a side (station-ops sends none while nothing is listed as missing) is support's.
const waitsOn = (record: ProvisioningSummary): string | null => {
  switch (record.status) {
    case "COLLECTING":
      return record.waitingOn ?? "SUPPORT";
    case "AWAITING_APPROVAL":
      return "SUPPORT";
    case "NEEDS_MANUAL_ACTION":
      return canMarkHandled(record) ? "SUPPORT" : null;
    default:
      return null;
  }
};

const isFor = (filter: Filter) => (record: ProvisioningSummary) => !filter.waitingOn || waitsOn(record) === filter.waitingOn;

/** Station requests by status, oldest first, so none is forgotten (SOPS-148). */
const ProvisioningQueue: React.FC = () => {
  const navigate = useNavigate();
  const [filterKey, setFilterKey] = useState("all");
  const [rows, setRows] = useState<QueueRow[]>([]);
  // station-ops pages each status separately, so "Load more" keeps a cursor per status.
  const [cursors, setCursors] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [marking, setMarking] = useState<string | null>(null);
  const generation = useRef(0);
  const filter = FILTERS.find((f) => f.key === filterKey) ?? FILTERS[0];

  const load = useCallback(
    async (from: Record<string, string | null> | null) => {
      const run = ++generation.current;
      const statuses = from ? Object.keys(from).filter((s) => from[s]) : filter.statuses;
      setLoading(true);
      setError(null);
      try {
        const pages = await Promise.all(
          statuses.map(async (status) => [status, await listProvisioning({ status, cursor: from?.[status] ?? null })] as const)
        );
        const added = await enrich(pages.flatMap(([, page]) => page.records).filter(isFor(filter)));
        if (run !== generation.current) return;
        setRows((current) => (from ? [...current, ...added] : added).sort(byOldest));
        setCursors((current) => ({
          ...(from ? current : {}),
          ...Object.fromEntries(pages.map(([status, page]) => [status, page.nextCursor])),
        }));
      } catch (err) {
        if (run === generation.current) setError(provisioningErrorMessage(err, "load"));
      } finally {
        if (run === generation.current) setLoading(false);
      }
    },
    [filter]
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  const hasMore = Object.values(cursors).some(Boolean);

  const markHandled = async (row: QueueRow) => {
    setMarking(row.stationId);
    setError(null);
    try {
      const { manualActionHandled } = await markProvisioningHandled(row.stationId);
      const updated = { ...row, manualActionHandled };
      setRows((current) =>
        current.flatMap((r) => (r.stationId !== row.stationId ? [r] : isFor(filter)(updated) ? [updated] : []))
      );
    } catch (err) {
      setError(provisioningErrorMessage(err, "handled"));
    } finally {
      setMarking(null);
    }
  };

  const status = (r: QueueRow) => (
    <>
      <ProvisioningStatusTag status={r.status} waitingOn={r.waitingOn} />
      {r.manualActionHandled && (
        <div className="text-sm text-color-secondary mt-1">
          Handled by {r.manualActionHandled.actorEmail ?? r.manualActionHandled.actorId} on{" "}
          {formatDate(r.manualActionHandled.at)}
        </div>
      )}
    </>
  );

  const action = (r: QueueRow) =>
    canMarkHandled(r) ? (
      <Button
        label="Mark as handled"
        size="small"
        outlined
        loading={marking === r.stationId}
        onClick={(e) => {
          e.stopPropagation();
          void markHandled(r);
        }}
      />
    ) : null;

  return (
    <div>
      <SelectButton
        className="mb-3"
        value={filterKey}
        options={FILTERS.map((f) => ({ label: f.label, value: f.key }))}
        onChange={(e) => e.value && setFilterKey(e.value)}
        allowEmpty={false}
        aria-label="Show"
      />
      {error && <Message severity="error" className="w-full mb-3" text={error} />}
      <DataTable
        value={rows}
        loading={loading}
        dataKey="stationId"
        emptyMessage={filter.empty}
        selectionMode="single"
        onRowClick={(e) => navigate(`/onboarding/prospects/${(e.data as QueueRow).prospectId}`)}
        rowClassName={() => "cursor-pointer"}
      >
        <Column header="Station" field="stationId" />
        <Column header="Location" body={address} />
        <Column header="Customer" body={(r: QueueRow) => r.customerName ?? "—"} />
        <Column header="Status" body={status} />
        <Column header="Attempt" field="attempt" />
        <Column header="Updated" body={(r: QueueRow) => formatDate(r.updatedAt)} />
        <Column body={action} />
      </DataTable>
      {hasMore && (
        <div className="flex justify-content-center mt-3">
          <Button label="Load more" outlined loading={loading} onClick={() => load(cursors)} />
        </div>
      )}
    </div>
  );
};

export default ProvisioningQueue;
